import {
    ChangeDetectionStrategy, Component, OnDestroy, OnInit,
    inject, signal, computed
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject, distinctUntilChanged, map, switchMap, takeUntil } from 'rxjs';
import { FieldExecutionService } from '../../../services/field-execution.service';
import { Project } from '../../../models/project.model';
import { splitExecutantNames, splitExecutantNumbers } from '../../../utils/executant-name.utils';

@Component({
    selector: 'app-executant-detail',
    imports: [CommonModule, FormsModule],
    templateUrl: './executant-detail.component.html',
    styleUrl: './executant-detail.component.css',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ExecutantDetailComponent implements OnInit, OnDestroy {
    private readonly router   = inject(Router);
    private readonly route    = inject(ActivatedRoute);
    private readonly svc      = inject(FieldExecutionService);
    private readonly destroy$ = new Subject<void>();
    private timerHandle: any;

    currentTime = signal(new Date());
    isLoading    = signal(true);
    allProjects  = signal<Project[]>([]);
    executantKey = signal('');
    searchTerm   = signal('');
    statusFilter = signal('');
    hmDrawerOpen = signal(false);
    selectedProject = signal<Project | null>(null);
    readonly today = new Date().toISOString().slice(0, 10);

    readonly statusOptions = ['', 'Planned', 'In Progress', 'Completed', 'Stuck'];

    // ── Identity ──────────────────────────────────────────────────────────────
    executantName = computed<string>(() => {
        // person: pages always show the individual's own name, not the joint entry
        if (this.executantKey().startsWith('person:')) return this.nameFromKey();
        const p = this.allProjects()[0];
        if (!p) return this.nameFromKey();
        return p.executorName || p.executingAgency || p.executant?.name || this.nameFromKey();
    });

    executantNumber = computed<string>(() => {
        const key = this.executantKey();
        if (key.startsWith('number:')) return key.slice(7);
        if (key.startsWith('person:')) return this.personNumber();
        const p = this.allProjects()[0];
        return p?.executorNumber || p?.executant?.contact || '';
    });

    /** Best-effort personal contact for a person: page — only trusted when the
     *  name and number lists of a project align unambiguously. */
    private personNumber(): string {
        const person = this.nameFromKey().trim().toLowerCase();
        if (!person) return '';
        for (const p of this.allProjects()) {
            const names = splitExecutantNames(p.executorName ?? p.executingAgency ?? p.executant?.name ?? '');
            const idx = names.findIndex(n => n.toLowerCase() === person);
            if (idx < 0) continue;
            const numbers = splitExecutantNumbers(p.executorNumber ?? p.executant?.contact ?? '');
            if (names.length === 1 && numbers.length) return numbers[0];
            if (numbers.length === names.length && numbers[idx]) return numbers[idx];
        }
        return '';
    }

    /** Individual members when this page shows a joint executant entry ("A & B"). */
    executantMembers = computed<string[]>(() => {
        if (this.executantKey().startsWith('person:')) return [];
        const parts = splitExecutantNames(this.executantName());
        return parts.length > 1 ? parts : [];
    });

    viewPerson(name: string): void {
        const person = name.trim();
        if (!person) return;
        // Forward the return-navigation state so Back still works from the person page.
        const st = history.state as { returnUrl?: string; returnGp?: string; returnScreen?: 'villages' | 'projects' } | undefined;
        void this.router.navigate(
            ['/field-execution/executant', `person:${encodeURIComponent(person)}`],
            { state: { returnUrl: st?.returnUrl, returnGp: st?.returnGp, returnScreen: st?.returnScreen } }
        );
    }

    executantInitials = computed<string>(() =>
        this.executantName().split(/\s+/).map(w => w[0] || '').join('').slice(0, 2).toUpperCase() || 'EX'
    );

    executantType = computed<string>(() => {
        const name = this.executantName() || '';
        if (name.toLowerCase().includes('committee')) return 'Committee';
        return splitExecutantNames(name).length > 1 ? 'Committee' : 'Individual';
    });

    primaryBlock = computed<string>(() => {
        const blocks = this.allProjects().map(p => p.block || '').filter(Boolean);
        if (!blocks.length) return '';
        const freq = new Map<string, number>();
        blocks.forEach(b => freq.set(b, (freq.get(b) || 0) + 1));
        return [...freq.entries()].sort((a, b) => b[1] - a[1])[0][0];
    });

    primaryGP = computed<string>(() => {
        const gps = this.allProjects().map(p => p.gp || p.village || p.loc || '').filter(Boolean);
        if (!gps.length) return '';
        const freq = new Map<string, number>();
        gps.forEach(g => freq.set(g, (freq.get(g) || 0) + 1));
        return [...freq.entries()].sort((a, b) => b[1] - a[1])[0][0];
    });

    // ── Stats ─────────────────────────────────────────────────────────────────
    totalProjects   = computed(() => this.allProjects().length);
    totalCost       = computed(() => this.allProjects().reduce((s, p) => s + (p.cost ?? 0), 0));
    completedCount  = computed(() => this.allProjects().filter(p => p.status === 'Completed').length);
    inProgressCount = computed(() => this.allProjects().filter(p => p.status === 'In Progress').length);
    plannedCount    = computed(() => this.allProjects().filter(p => p.status === 'Planned').length);
    stuckCount      = computed(() => this.allProjects().filter(p => p.status === 'Stuck').length);
    onTrackCount    = computed(() => this.allProjects().filter(p => p.status === 'Completed').length);
    delayedCount    = computed(() => this.allProjects().filter(p => p.status === 'Stuck').length);

    rivalryCount = computed(() =>
        this.allProjects().filter(p => this.getIssueType(p) === 'Local Rivalry').length
    );

    delayPercent = computed(() => {
        const t = this.totalProjects();
        return t > 0 ? Math.round((this.delayedCount() / t) * 100) : 0;
    });

    riskScore = computed(() => {
        const t = this.totalProjects();
        if (!t) return 0;
        return Math.min(100, Math.round((this.delayedCount() / t) * 60 + (this.rivalryCount() / t) * 40));
    });

    riskLevel = computed<'HIGH' | 'MEDIUM' | 'LOW'>(() => {
        const s = this.riskScore();
        return s >= 50 ? 'HIGH' : s >= 25 ? 'MEDIUM' : 'LOW';
    });

    // ── Filtered projects ─────────────────────────────────────────────────────
    filteredProjects = computed<Project[]>(() => {
        const q  = this.searchTerm().toLowerCase().trim();
        const sf = this.statusFilter();
        return this.allProjects().filter(p => {
            const gp = p.gp || p.village || p.loc || '';
            const matchQ  = !q  || p.name.toLowerCase().includes(q) || gp.toLowerCase().includes(q) || (p.scheme || '').toLowerCase().includes(q);
            const matchSf = !sf || p.status === sf;
            return matchQ && matchSf;
        });
    });

    // ── Issue breakdown ───────────────────────────────────────────────────────
    issueBreakdown = computed(() => {
        const ps = this.allProjects();
        const total = ps.length;
        const counts: Record<string, number> = {};
        for (const p of ps) {
            const t = this.getIssueType(p);
            const label = t === '–' ? 'No Issue' : t;
            counts[label] = (counts[label] || 0) + 1;
        }
        return Object.entries(counts)
            .map(([label, count]) => ({
                label, count, pct: total > 0 ? Math.round((count / total) * 100) : 0,
                icon: this.issueIcon(label), color: this.issueColor(label)
            }))
            .sort((a, b) => {
                if (a.label === 'No Issue') return 1;
                if (b.label === 'No Issue') return -1;
                return b.count - a.count;
            });
    });

    // ── GP breakdown ─────────────────────────────────────────────────────────
    gpBreakdown = computed(() => {
        const map = new Map<string, number>();
        for (const p of this.allProjects()) {
            const gp = p.gp || p.village || p.loc || 'Unknown';
            map.set(gp, (map.get(gp) || 0) + 1);
        }
        return [...map.entries()].map(([gp, count]) => ({ gp, count })).sort((a, b) => b.count - a.count);
    });

    // ── Update discipline ─────────────────────────────────────────────────────
    lastUpdatedDays = computed<number | null>(() => {
        const dates = this.allProjects()
            .map(p => this.tsToMs(p.updatedAt ?? p.photoUploadedAt))
            .filter((ms): ms is number => ms !== null && !isNaN(ms));
        if (!dates.length) return null;
        return Math.floor((Date.now() - Math.max(...dates)) / 86400000);
    });

    updateConsistency = computed<'Good' | 'Moderate' | 'Poor'>(() => {
        const d = this.lastUpdatedDays();
        if (d === null) return 'Poor';
        if (d <= 2)  return 'Good';
        if (d <= 7)  return 'Moderate';
        return 'Poor';
    });

    // ── Donut chart ───────────────────────────────────────────────────────────
    donutSegments = computed(() => {
        const total = this.totalProjects();
        if (!total) return [];
        const r = 40, circ = 2 * Math.PI * r;
        const items = [
            { label: 'Completed',   count: this.onTrackCount(),    color: '#16a34a' },
            { label: 'In Progress', count: this.inProgressCount(), color: '#d97706' },
            { label: 'Delayed',     count: this.delayedCount(),    color: '#dc2626' },
            { label: 'Not Started', count: this.plannedCount(),    color: '#94a3b8' },
        ];
        let offset = 0;
        return items.map(item => {
            const len = (item.count / total) * circ;
            const seg = { ...item, pct: Math.round((item.count / total) * 100), dasharray: `${len} ${circ}`, dashoffset: -offset };
            offset += len;
            return seg;
        });
    });

    // ── Trend data ────────────────────────────────────────────────────────────
    trendData = computed(() => {
        const months = ["Nov '24", "Dec '24", "Jan '25", "Feb '25", "Mar '25", "Apr '25"];
        const on = this.onTrackCount(), ip = this.inProgressCount();
        const dl = this.delayedCount(), ns = this.plannedCount();
        const vary = (base: number, factor: number) =>
            months.map((_, i) => Math.max(0, Math.round(base + Math.sin(i * 1.2 + base) * factor)));
        return {
            months,
            series: [
                { label: 'Completed',   color: '#16a34a', values: vary(on, 0.8) },
                { label: 'In Progress', color: '#d97706', values: vary(ip, 0.8) },
                { label: 'Delayed',     color: '#dc2626', values: vary(dl, 1.2) },
                { label: 'Not Started', color: '#94a3b8', values: vary(ns, 0.5) },
            ]
        };
    });

    trendMaxVal = computed(() => Math.max(1, ...this.trendData().series.flatMap(s => s.values)));

    // ── Insights & actions ────────────────────────────────────────────────────
    insights = computed(() => {
        const total = this.totalProjects(), delayed = this.delayedCount(), rivalry = this.rivalryCount();
        const gps = new Set(this.allProjects().map(p => p.village || p.loc || '')).size;
        const items: { icon: string; color: string; text: string }[] = [];
        if (delayed > 0)
            items.push({ icon: 'fa-circle', color: '#dc2626', text: `${delayed} out of ${total} project${total !== 1 ? 's' : ''} are delayed.` });
        if (rivalry > 0)
            items.push({ icon: 'fa-people-arrows', color: '#ea580c', text: `Rivalry issues in ${rivalry} project${rivalry !== 1 ? 's' : ''} across ${gps} GP${gps !== 1 ? 's' : ''}.` });
        if (delayed > 0)
            items.push({ icon: 'fa-clock', color: '#d97706', text: `Average delay in delayed projects: ${Math.round(total * 3 + 5)} days.` });
        if (this.delayPercent() >= 40)
            items.push({ icon: 'fa-circle-info', color: '#3b82f6', text: 'Avoid assigning high value works until improvement.' });
        return items;
    });

    recommendedActions = computed(() => {
        const actions: { icon: string; text: string }[] = [];
        if (this.delayedCount() > 0)
            actions.push({ icon: 'fa-magnifying-glass', text: 'Field verification required for delayed projects.' });
        if (this.rivalryCount() > 0)
            actions.push({ icon: 'fa-people-arrows', text: 'Resolve rivalry issues in coordination with local administration.' });
        actions.push({ icon: 'fa-calendar-check', text: 'Monitor weekly and review progress.' });
        return actions;
    });

    ngOnInit(): void {
        // Subscribe to param changes (not just the snapshot): navigating from a
        // joint executant page to a member's person: page reuses this component.
        this.route.paramMap
            .pipe(
                map(pm => pm.get('key') ?? ''),
                distinctUntilChanged(),
                switchMap(key => {
                    this.executantKey.set(key);
                    this.isLoading.set(true);
                    // Reset per-key UI state — navigating between executants (e.g. a
                    // joint entry to a member's own page) reuses this component instance.
                    this.searchTerm.set('');
                    this.statusFilter.set('');
                    this.selectedProject.set(null);
                    this.hmDrawerOpen.set(false);
                    return this.svc.getProjectsByExecutantKey$(key);
                }),
                takeUntil(this.destroy$)
            )
            .subscribe({
                next: projects => {
                    // Dedup is handled entirely in the service
                    this.allProjects.set(projects);
                    this.isLoading.set(false);
                },
                error: () => this.isLoading.set(false)
            });

        this.timerHandle = setInterval(() => {
            this.currentTime.set(new Date());
        }, 60000);
    }

    ngOnDestroy(): void {
        if (this.timerHandle) clearInterval(this.timerHandle);
        this.destroy$.next(); this.destroy$.complete();
    }

    goHome(): void { this.router.navigate(['/home']); }

    goBack(): void {
        const state = history.state as {
            returnUrl?: string;
            returnGp?: string;
            returnScreen?: 'villages' | 'projects';
        } | undefined;

        const returnUrl = state?.returnUrl;
        const returnGp = state?.returnGp?.trim() || this.primaryGP().trim();
        const returnScreen = state?.returnScreen ?? (returnGp ? 'projects' : 'villages');
        const listState = returnScreen === 'projects' && returnGp
            ? { openGp: returnGp, screen: 'projects' as const }
            : undefined;

        if (returnUrl) {
            void this.router.navigateByUrl(returnUrl, { state: listState });
            return;
        }

        const block = this.primaryBlock();
        if (block && listState) {
            void this.router.navigate(['/field-execution', encodeURIComponent(block)], { state: listState });
            return;
        }
        if (block) {
            void this.router.navigate(['/field-execution', encodeURIComponent(block)]);
            return;
        }
        void this.router.navigate(['/field-execution']);
    }

    openHmDrawer(): void { this.hmDrawerOpen.set(true); }
    closeHmDrawer(): void { this.hmDrawerOpen.set(false); }

    openProjectDetail(p: Project): void { this.selectedProject.set(p); }
    closeProjectDetail(): void { this.selectedProject.set(null); }

    costCompact(amount: number | undefined | null): string {
        const v = amount ?? 0;
        if (v >= 100) return `₹${Math.round(v / 100)}Cr`;
        if (v >= 10) return `₹${Math.round(v)}L`;
        return `₹${v.toFixed(2)}L`;
    }

    riskShort(): string {
        const l = this.riskLevel();
        return l === 'LOW' ? 'L' : l === 'MEDIUM' ? 'M' : 'H';
    }

    exportPdf(): void {
        this.svc.exportExecutantToPdf({
            name:            this.executantName(),
            number:          this.executantNumber(),
            block:           this.primaryBlock(),
            gp:              this.primaryGP(),
            type:            this.executantType(),
            totalProjects:   this.totalProjects(),
            totalCost:       this.totalCost(),
            riskLevel:       this.riskLevel(),
            riskScore:       this.riskScore(),
            completedCount:  this.completedCount(),
            inProgressCount: this.inProgressCount(),
            stuckCount:      this.stuckCount(),
            plannedCount:    this.plannedCount(),
            lastUpdatedDays: this.lastUpdatedDays(),
            projects:        this.allProjects(),
        });
    }

    // ── UI helpers ────────────────────────────────────────────────────────────
    getIssueType(p: Project): string {
        const r = (p.remarks || '').toLowerCase();
        if (r.includes('rival') || r.includes('dispute')) return 'Local Rivalry';
        if (r.includes('fund') || r.includes('uc pending')) return 'Fund Pending';
        if (r.includes('contractor')) return 'Contractor Delay';
        if (r.includes('administrative') || r.includes('tech sanction') || r.includes('sanction awaited')) return 'Administrative Delay';
        if (r.includes('land')) return 'Land Issue';
        return '–';
    }

    statusLabel(status: string): string {
        return ({ 'Completed': 'On Track', 'In Progress': 'In Progress', 'Stuck': 'Delayed', 'Planned': 'Not Started' } as Record<string, string>)[status] || status;
    }

    statusChipClass(status: string): string {
        return ({ 'Completed': 'chip-on-track', 'In Progress': 'chip-in-progress', 'Stuck': 'chip-delayed', 'Planned': 'chip-planned' } as Record<string, string>)[status] || 'chip-planned';
    }

    issueIcon(label: string): string {
        const l = label.toLowerCase();
        if (l.includes('rivalry')) return 'fa-people-arrows';
        if (l.includes('fund')) return 'fa-indian-rupee-sign';
        if (l.includes('contractor')) return 'fa-building';
        if (l.includes('administrative')) return 'fa-file-circle-xmark';
        if (l.includes('land')) return 'fa-mountain-sun';
        if (l.includes('no issue')) return 'fa-circle-check';
        return 'fa-circle-info';
    }
    issueColor(label: string): string {
        const l = label.toLowerCase();
        if (l.includes('rivalry')) return '#f59e0b'; // Amber
        if (l.includes('fund')) return '#f97316'; // Orange
        if (l.includes('contractor')) return '#64748b'; // Slate
        if (l.includes('administrative')) return '#64748b'; // Slate
        if (l.includes('land')) return '#8b5cf6'; // Purple
        if (l.includes('no issue')) return '#16a34a'; // Green
        return '#94a3b8';
    }

    getLastUpdatedLabel(days: number | null): string {
        if (days === null) return 'N/A';
        if (days === 0) return 'Today';
        return `${days} day${days !== 1 ? 's' : ''} ago`;
    }

    getDaysLabel(ts: any): string {
        const ms = this.tsToMs(ts);
        if (ms === null || isNaN(ms)) return '—';
        const days = Math.floor((Date.now() - ms) / 86400000);
        return days === 0 ? 'Today' : `${days} day${days !== 1 ? 's' : ''} ago`;
    }

    private tsToMs(ts: any): number | null {
        if (!ts) return null;
        if (typeof ts?.toDate === 'function') return ts.toDate().getTime();
        if (typeof ts?.seconds === 'number')   return ts.seconds * 1000;
        if (ts instanceof Date)                return ts.getTime();
        if (typeof ts === 'number')            return ts;
        if (typeof ts === 'string')            return new Date(ts).getTime();
        return null;
    }

    trendPath(values: number[], maxVal: number): string {
        if (!values.length || maxVal === 0) return '';
        const w = 360, h = 100, padX = 0, padY = 10;
        const uw = w / (values.length - 1);
        return values.map((v, i) => `${i === 0 ? 'M' : 'L'} ${padX + i * uw},${h - padY - (v / maxVal) * (h - padY * 2)}`).join(' ');
    }

    private nameFromKey(): string {
        const key = this.executantKey();
        if (key.startsWith('person:')) return decodeURIComponent(key.slice(7));
        return key.startsWith('name:') ? decodeURIComponent(key.slice(5)) : '';
    }
}
